/**
 * This file provides a global refresh system for the Weave app.
 *
 * Components can use the `useAutoRefresh` hook to automatically refetch data
 * when a global refresh is triggered. The refresh is triggered by calling the
 * `triggerRefresh` function from the `useRefresh` hook.
 *
 * Example usage:
 *
 * ```tsx
 * // In a component that displays data:
 * const {useCall} = useWFHooks();
 * const callData = useCall({key: {entity, project, callId}});
 * useAutoRefresh(callData.refetch);
 *
 * // Multiple refetches - just call the hook multiple times:
 * const callData = useCall({...});
 * const callsData = useCalls({...});
 * useAutoRefresh(callData.refetch);
 * useAutoRefresh(callsData.refetch);
 *
 * // In a component with a refresh button:
 * const {triggerRefresh, isRefreshing} = useRefresh();
 * <Button onClick={triggerRefresh} loading={isRefreshing}>
 *   Refresh
 * </Button>
 * ```
 */

import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface RefreshContextValue {
  // A counter that increments each time refresh is triggered
  refreshVersion: number;
  // Trigger a global refresh
  triggerRefresh: () => void;
  // Whether any refresh operations are in progress
  isRefreshing: boolean;
  // Register a refresh operation (for tracking loading state)
  registerRefreshOperation: (operation: Promise<any>) => void;
}

const RefreshContext = createContext<RefreshContextValue | null>(null);

/**
 * Provider component that wraps the app to enable global refresh functionality.
 */
export const RefreshProvider: FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [activeOperations, setActiveOperations] = useState(0);
  const operationCountRef = useRef(0);

  const triggerRefresh = useCallback(() => {
    setRefreshVersion(v => v + 1);
  }, []);

  const registerRefreshOperation = useCallback((operation: Promise<any>) => {
    operationCountRef.current += 1;
    setActiveOperations(operationCountRef.current);

    // Decrement when done (whether success or failure)
    operation.finally(() => {
      operationCountRef.current -= 1;
      setActiveOperations(operationCountRef.current);
    });
  }, []);

  const value = useMemo(
    () => ({
      refreshVersion,
      triggerRefresh,
      isRefreshing: activeOperations > 0,
      registerRefreshOperation,
    }),
    [refreshVersion, triggerRefresh, activeOperations, registerRefreshOperation]
  );

  return (
    <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>
  );
};

const NOOP_REFRESH_VALUE: RefreshContextValue = {
  refreshVersion: 0,
  triggerRefresh: () => {},
  isRefreshing: false,
  registerRefreshOperation: () => {},
};

/**
 * Hook to access refresh functionality.
 *
 * If used outside of a RefreshProvider, returns a no-op implementation.
 *
 * @returns An object with:
 *  - triggerRefresh: Function to trigger a global refresh
 *  - isRefreshing: Whether any refresh operations are in progress
 *  - refreshVersion: The current refresh version (increments on each refresh)
 */
export const useRefresh = () => {
  const ctx = useContext(RefreshContext);

  return ctx ?? NOOP_REFRESH_VALUE;
};

/**
 * Hook that automatically calls a refetch function when global refresh is triggered.
 *
 * If you need to refresh multiple data sources, call this hook multiple times.
 *
 * @param refetch - The refetch function to call
 * @param options - Optional configuration
 * @param options.enabled - Whether auto-refresh is enabled (default: true)
 *
 * @example
 * ```tsx
 * const callData = useCall({key: {entity, project, callId}});
 * useAutoRefresh(callData.refetch);
 * ```
 */
export const useAutoRefresh = (
  refetch: (() => Promise<any>) | (() => void),
  options?: {enabled?: boolean}
) => {
  const {refreshVersion, registerRefreshOperation} = useRefresh();
  const enabled = options?.enabled ?? true;

  // Track the last refresh version we responded to
  const lastRefreshVersionRef = useRef(refreshVersion);

  useEffect(() => {
    // Skip if not enabled or we've already responded to this refresh version
    if (!enabled || lastRefreshVersionRef.current === refreshVersion) {
      return;
    }

    // Update our last responded version
    lastRefreshVersionRef.current = refreshVersion;

    // Call refetch and register the operation for loading state tracking
    const refetchResult = refetch();
    if (refetchResult instanceof Promise) {
      registerRefreshOperation(refetchResult);
    } else {
      registerRefreshOperation(Promise.resolve());
    }
  }, [refreshVersion, refetch, enabled, registerRefreshOperation]);
};
